import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Switch, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useDownloadInstitutionsExport,
  useFilterInstitutions,
  useUpdateInstitutionSettings,
} from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions } from "../../../auth/hooks";
import { INSTITUTION_ROUTE_PATHS } from "../../paths";
import { InstitutionFilterPageRequest, InstitutionList } from "../../types";
import InstitutionTypeTagWithName from "../InstitutionTypeTagWithName";

export const InstitutionListTableView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParams = useFilterSearchParams<InstitutionFilterPageRequest>({ params: ["types"] });

  const { data } = useFilterInstitutions(searchParams);
  const updateInstitutionSettings = useUpdateInstitutionSettings();
  const downloadInstitutionsExport = useDownloadInstitutionsExport();

  const hasAdminGlobalSettingsPermission = useHasPermissions(Permission.ADMIN_GLOBAL_ENUMERATIONS);

  const handleUpdateInstitutionSettings = async (institution: InstitutionList) => {
    if (institution.settings) {
      await updateInstitutionSettings.mutateAsync({
        id: institution.id,
        object: {
          optimisticLockVersion: institution.settings.optimisticLockVersion,
          deactivated: !institution.settings.deactivated,
        },
      });
    }
  };

  const columns: ColumnsType<InstitutionList> = [
    {
      key: "name",
      title: t("institution.attrs.name"),
      width: 260,
      ellipsis: { showTitle: false },
      render: (_, record) => <InstitutionTypeTagWithName institution={record} ellipsis />,
    },
    {
      key: "officialName",
      title: t("institution.attrs.officialName"),
      width: 450,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.officialName}</Ellipsis>,
    },
    {
      key: "activated",
      title: (
        <LabelWithTooltip label={t("institution.attrs.activated")} tooltip={t("institution.helpers.deactivatedDesc")} />
      ),
      align: "center",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Switch
          size="small"
          checked={!record.settings?.deactivated}
          onChange={() => handleUpdateInstitutionSettings(record)}
        />
      ),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) =>
        hasAdminGlobalSettingsPermission && (
          <ActionTextIcon
            path={generatePath(INSTITUTION_ROUTE_PATHS.detail.to, { id: record.id })}
            icon="edit"
            color="blue"
            text={t("common.edit")}
          />
        ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card className="card-box">
      <div className={numberOrZero(data?.totalElementsCount) ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() => downloadInstitutionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })}
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() => downloadInstitutionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })}
        />
      </div>

      <Table<InstitutionList>
        {...tableStandardProps()}
        columns={columns}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};
